import React, { useEffect, useRef } from 'react';
import * as d3 from 'd3';

const ParetoChart = ({
  width = 800,
  height = 400,
  data = {},
  type = "Dataset",
  selectedMetric = null,
}) => {
  const svgRef = useRef();

  useEffect(() => {
    if (!data || Object.keys(data).length === 0) return;

    let formattedData = [];

    //  Handle Dataset case
    if (type === "Dataset") {
      formattedData = Object.values(data)
        .filter(metric => metric && typeof metric.value === "number" && !isNaN(metric.value))
        .map(metric => ({
          category: metric.name,
          value: metric.value,
        }));
    } 
    //  Handle Attribute case
    else if (type === "Attribute") {
      let contributionData = [];

      // Case 1: array-of-arrays like [[375107,88],[375059,42]]
      if (Array.isArray(data) && Array.isArray(data[0])) {
        contributionData = data;
      } 

      formattedData = contributionData
        .filter(([name, val]) => typeof val === "number" && !isNaN(val))
        .map(([name, val]) => ({
          category: String(name),
          value: val,
        }));
    }

    if (formattedData.length === 0) return;

    //  D3 setup
    const svg = d3.select(svgRef.current);
    svg.selectAll('*').remove();

    // Add font-family to SVG
    svg.style("font-family", "Poppins");

    const margin = { top: 30, right: 50, bottom: 70, left: 50 };
    const innerWidth = width - margin.left - margin.right;
    const innerHeight = height - margin.top - margin.bottom;

    const chart = svg
      .attr('width', width)
      .attr('height', height)
      .append('g')
      .attr('transform', `translate(${margin.left},${margin.top})`);

    //  Sort descending
    formattedData.sort((a, b) => b.value - a.value);

    //  Cumulative %
    const total = d3.sum(formattedData, d => d.value);
    let cumulative = 0;
    formattedData.forEach(d => {
      cumulative += d.value;
      d.cumulative = total > 0 ? (cumulative / total) * 100 : 0;
    });

    //  Scales
    const x = d3.scaleBand()
      .domain(formattedData.map(d => d.category))
      .range([0, innerWidth])
      .padding(0.2);

    const yLeft = d3.scaleLinear()
      .domain([0, d3.max(formattedData, d => d.value)])
      .nice()
      .range([innerHeight, 0]);

    const yRight = d3.scaleLinear()
      .domain([0, 100])
      .range([innerHeight, 0]);

    // Add horizontal grid lines for bar chart values
    const yTicks = yLeft.ticks(5);
    chart.selectAll(".horizontal-grid")
      .data(yTicks)
      .join("line")
      .attr("class", "horizontal-grid")
      .attr("x1", 0)
      .attr("x2", innerWidth)
      .attr("y1", d => yLeft(d))
      .attr("y2", d => yLeft(d))
      .attr("stroke", "rgba(0, 0, 0, 0.12)")
      .attr("stroke-width", 1)
      .lower(); // Places grid lines behind other elements

    //  Left Y-axis
    chart.append('g')
      .call(d3.axisLeft(yLeft))
      .selectAll("text")
      .style("font-size", "12px")
      .style("color", "rgba(0, 0, 0, 0.6)");

    //  Right Y-axis
    chart.append('g')
      .attr('transform', `translate(${innerWidth},0)`)
      .call(d3.axisRight(yRight).ticks(10).tickFormat(d => d + "%"))
      .selectAll("text")
      .style("font-size", "12px")
      .style("color", "rgba(0, 0, 0, 0.6)");

    //  X-axis
    chart.append('g')
      .attr('transform', `translate(0,${innerHeight})`)
      .call(d3.axisBottom(x))
      .selectAll("text")
      .attr("transform", "rotate(-30)")
      .style("text-anchor", "end")
      .style("font-size", "9px")
      .style("color", "rgba(0, 0, 0, 0.6)");

    //  Style axis lines - remove left and right domain lines, keep tick lines
    chart.selectAll(".domain").remove(); // This removes all domain lines (left, right, bottom)
    
    chart.selectAll(".tick line")
      .style("stroke", "rgba(0, 0, 0, 0.12)");

    //  Tooltip
    const tooltip = d3.select("body")
      .append("div")
      .style("position", "absolute")
      .style("background", "#fff")
      .style("padding", "6px 10px")
      .style("border", "1px solid #ccc")
      .style("border-radius", "4px")
      .style("pointer-events", "none")
      .style("opacity", 0)
      .style("font-family", "Poppins");

    //  Bars with new color and rounded tops
    chart.selectAll(".bar")
      .data(formattedData)
      .join("rect")
      .attr("class", "bar")
      .attr("fill", "rgba(137, 101, 240, 0.75)")
      .attr("x", d => x(d.category))
      .attr("y", d => yLeft(d.value))
      .attr("width", x.bandwidth())
      .attr("height", d => innerHeight - yLeft(d.value))
      .attr("rx", 4) // Horizontal radius for rounded corners
      .attr("ry", 4); // Vertical radius for rounded corners

    //  Line with new color
    const line = d3.line()
      .x(d => x(d.category) + x.bandwidth() / 2)
      .y(d => yRight(d.cumulative))
      .curve(d3.curveMonotoneX);

    chart.append("path")
      .datum(formattedData)
      .attr("fill", "none")
      .attr("stroke", "rgba(255, 99, 132, 1)")
      .attr("stroke-width", 2)
      .attr("d", line);

    //  Labels
    chart.selectAll(".label")
      .data(formattedData)
      .join("text")
      .attr("x", d => x(d.category) + x.bandwidth() / 2)
      .attr("y", d => yRight(d.cumulative) - 5)
      .attr("text-anchor", "middle")
      .attr("font-size", "10px")
      .attr("fill", "rgba(0, 0, 0, 0.6)")
      .style("font-family", "Poppins")
      .text(d => `${d.cumulative.toFixed(0)}%`);

    // // Alternative: Remove only left and right domain lines, keep bottom
    // chart.select(".domain").remove(); // Removes the first domain line found
    // chart.selectAll("g").selectAll(".domain")
    //   .filter(function() {
    //     return d3.select(this.parentNode).attr("transform") !== `translate(0,${innerHeight})`;
    //   })
    //   .remove();

    return () => tooltip.remove();
  }, [data, type, selectedMetric, width, height]);

  return <svg ref={svgRef}></svg>;
};

export default ParetoChart;

// import React, { useEffect, useRef } from 'react';
// import * as d3 from 'd3';

// const ParetoChart = ({ 
//                         width = 800, 
//                         height = 400,
//                         data=[
//                                 { category: 'A', value: 40 },
//                                 { category: 'B', value: 25 },
//                                 { category: 'C', value: 20 },
//                                 { category: 'D', value: 10 },
//                                 { category: 'E', value: 5 },
//                         ],
//                         type="Dataset"
// }) => {
//   const svgRef = useRef();


//   useEffect(() => {

//     //console.log(JSON.stringify(data))

//     // const convertedData = {};

//     // top_20_frequency.forEach((item, index) => {
//     //   const date = item[0];
//     //   const value = item[1];
//     //   convertedData[index] = { name: date, value: value };
//     // });

//     console.log("**************")
//     console.log(JSON.stringify(data))
//     console.log("**************")
//     let formattedData = []

//     if(type === "Attribute" && Array.isArray(data)){
//       formattedData = data.map((data)=> {
//                         return { category: type,cumulative : 0, name: data[0], value: data[1]}
//                     })
//     }
//     else{
//           formattedData = Object.values(data).map(item => ({
//             category: item.category,
//             value: item.value,
//             cumulative : 0,
//           }));
//     }

//     const svg = d3.select(svgRef.current);
//     svg.selectAll('*').remove();

//     const margin = { top: 30, right: 50, bottom: 70, left: 50 };
//     const innerWidth = width - margin.left - margin.right;
//     const innerHeight = height - margin.top - margin.bottom;

//     const chart = svg
//       .attr('width', width)
//       .attr('height', height)
//       .append('g')
//       .attr('transform', `translate(${margin.left},${margin.top})`);

//     // Sort by descending value
//     formattedData.sort((a, b) => b.value - a.value);

//     // Cumulative percentage
//     const total = d3.sum(formattedData, d => d.value);
//     let cumulative = 0;
//     formattedData.forEach(d => {
//       cumulative += d.value;
//       d.cumulative = (cumulative / total) * 100;
//     });

//     // Scales
//     const x = d3.scaleBand()
//       .domain(formattedData.map(d => d.category))
//       .range([0, innerWidth])
//       .padding(0.1);

//     const yLeft = d3.scaleLinear()
//       .domain([0, d3.max(formattedData, d => d.value)])
//       .nice()
//       .range([innerHeight, 0]);

//     const yRight = d3.scaleLinear()
//       .domain([0, 100])
//       .range([innerHeight, 0]);

//     // Axes
//     chart.append('g').call(d3.axisLeft(yLeft));

//     chart.append('g')
//       .attr('transform', `translate(${width},0)`)
//       .call(d3.axisRight(yRight).ticks(10).tickFormat(d => d + "%"));

//     chart.append('g')
//       .attr('transform', `translate(0,${height})`)
//       .call(d3.axisBottom(x))
//       .selectAll("text")
//       .attr("transform", "rotate(-30)")
//       .style("text-anchor", "end");

//     // Tooltip
//     const tooltip = d3.select("body")
//       .append("div")
//       .style("position", "absolute")
//       .style("background", "#fff")
//       .style("padding", "5px 10px")
//       .style("border", "1px solid #ccc")
//       .style("border-radius", "5px")
//       .style("pointer-events", "none")
//       .style("opacity", 0);

//     // Bars
//     chart.selectAll(".bar")
//       .data(formattedData)
//       .join("rect")
//       .attr("class", "bar")
//       .attr("fill", "steelblue")
//       .attr("x", d => x(d.category) + (x.bandwidth() - 10) / 2) // Center the 10px bar
//       .attr("y", d => yLeft(d.value))
//       .attr("width", 10) // Fixed bar width 10px
//       .attr("height", d => innerHeight - yLeft(d.value))
//       .on("mouseover", (event, d) => {
//         tooltip.transition().duration(200).style("opacity", 1);
//         tooltip.html(`Value: ${d.value}<br/>Cumulative: ${d.cumulative!==undefined && d.cumulative.toFixed(1)}%`)
//           .style("left", `${event.pageX + 10}px`)
//           .style("top", `${event.pageY - 28}px`);
//       })
//       .on("mouseout", () => {
//         tooltip.transition().duration(200).style("opacity", 0);
//       });

//     // Line
//     const line = d3.line()
//       .x(d => x(d.category) + x.bandwidth() / 2)
//       .y(d => yRight(d.cumulative));

//     chart.append("path")
//       .datum(formattedData)
//       .attr("class", "line")
//       .attr("fill", "none")
//       .attr("stroke", "orange")
//       .attr("stroke-width", 2)
//       .attr("d", line);

//     // Cumulative labels
//     chart.selectAll(".label")
//       .data(formattedData)
//       .join("text")
//       .attr("x", d => x(d.category) + x.bandwidth() / 2)
//       .attr("y", d => yRight(d.cumulative) - 5)
//       .attr("text-anchor", "middle")
//       .attr("font-size", "10px")
//       .text(d => d.cumulative.toFixed(0) + "%");

//     // Clean up tooltip on unmount
//     return () => {
//       tooltip.remove();
//     };

//   }, 
//   // [width, height,data]
// );

//   return (
//     <div>
//       <svg ref={svgRef}></svg>
//     </div>
//   );
// };

// export default ParetoChart;



// import React, { useEffect, useRef } from 'react';
// import * as d3 from 'd3';

// const ParetoChart = () => {
//   const svgRef = useRef();

//   useEffect(() => {
//     const data = [
//       { category: 'A', value: 40 },
//       { category: 'B', value: 25 },
//       { category: 'C', value: 20 },
//       { category: 'D', value: 10 },
//       { category: 'E', value: 5 }
//     ];

//     const svg = d3.select(svgRef.current);
//     svg.selectAll('*').remove();

//     const margin = { top: 30, right: 50, bottom: 70, left: 50 };
//     const width = 800 - margin.left - margin.right;
//     const height = 400 - margin.top - margin.bottom;

//     const chart = svg
//       .attr('width', width + margin.left + margin.right)
//       .attr('height', height + margin.top + margin.bottom)
//       .append('g')
//       .attr('transform', `translate(${margin.left},${margin.top})`);

//     // Sort by descending value
//     data.sort((a, b) => b.value - a.value);

//     // Cumulative percentage
//     const total = d3.sum(data, d => d.value);
//     let cumulative = 0;
//     data.forEach(d => {
//       cumulative += d.value;
//       d.cumulative = (cumulative / total) * 100;
//     });

//     // Scales
//     const x = d3.scaleBand()
//       .domain(data.map(d => d.category))
//       .range([0, width])
//       .padding(0.1);

//     const yLeft = d3.scaleLinear()
//       .domain([0, d3.max(data, d => d.value)])
//       .nice()
//       .range([height, 0]);

//     const yRight = d3.scaleLinear()
//       .domain([0, 100])
//       .range([height, 0]);

//     // Axes
//     chart.append('g').call(d3.axisLeft(yLeft));

//     chart.append('g')
//       .attr('transform', `translate(${width},0)`)
//       .call(d3.axisRight(yRight).ticks(10).tickFormat(d => d + "%"));

//     chart.append('g')
//       .attr('transform', `translate(0,${height})`)
//       .call(d3.axisBottom(x))
//       .selectAll("text")
//       .attr("transform", "rotate(-30)")
//       .style("text-anchor", "end");

//     // Tooltip
//     const tooltip = d3.select("body")
//       .append("div")
//       .style("position", "absolute")
//       .style("background", "#fff")
//       .style("padding", "5px 10px")
//       .style("border", "1px solid #ccc")
//       .style("border-radius", "5px")
//       .style("pointer-events", "none")
//       .style("opacity", 0);

//     // Bars
//     chart.selectAll(".bar")
//       .data(data)
//       .join("rect")
//       .attr("class", "bar")
//       .attr("fill", "steelblue")
//       .attr("x", d => x(d.category))
//       .attr("y", d => yLeft(d.value))
//       .attr("width", x.bandwidth())
//       .attr("height", d => height - yLeft(d.value))
//       .on("mouseover", (event, d) => {
//         tooltip.transition().duration(200).style("opacity", 1);
//         tooltip.html(`Value: ${d.value}<br/>Cumulative: ${d.cumulative.toFixed(1)}%`)
//           .style("left", `${event.pageX + 10}px`)
//           .style("top", `${event.pageY - 28}px`);
//       })
//       .on("mouseout", () => {
//         tooltip.transition().duration(200).style("opacity", 0);
//       });

//     // Line
//     const line = d3.line()
//       .x(d => x(d.category) + x.bandwidth() / 2)
//       .y(d => yRight(d.cumulative));

//     chart.append("path")
//       .datum(data)
//       .attr("class", "line")
//       .attr("fill", "none")
//       .attr("stroke", "orange")
//       .attr("stroke-width", 2)
//       .attr("d", line);

//     // Cumulative labels
//     chart.selectAll(".label")
//       .data(data)
//       .join("text")
//       .attr("x", d => x(d.category) + x.bandwidth() / 2)
//       .attr("y", d => yRight(d.cumulative) - 5)
//       .attr("text-anchor", "middle")
//       .attr("font-size", "10px")
//       .text(d => d.cumulative.toFixed(0) + "%");
//   }, []);

//   return (
//     <div>
//       <svg ref={svgRef}></svg>
//     </div>
//   );
// };

// export default ParetoChart;



// import ParetoChart from './ParetoChart'; // Adjust the path if needed

// function App() {
//   return (
//     <div className="App">
//       <h2>Pareto Chart</h2>
//       <ParetoChart />
//     </div>
//   );
// }

// export default App;
